/**
 * BugDetailModal — the full-detail view for one reported bug.
 *
 * Story: epic-uat-portal-001-11.
 *
 * On mount it fetches `getBug(id)` and renders the title, status pill, the
 * label/value fields, and the screenshots. It destructures EXACTLY the
 * whitelisted keys of the member-safe detail — it never spreads an unknown
 * payload into the DOM — so even if a reporter/email/contact field somehow rode
 * along, it has no render path here (defence in depth; the service already
 * projected it out).
 *
 * Screenshots are authed bytes behind a proxy, so each one is fetched with the
 * bearer (`fetchScreenshotBlob`), turned into an object URL, and set as the
 * <img> src. The object URLs created for a given `detail` are tracked per run
 * and REVOKED when that run tears down — on unmount (modal close) AND when the
 * modal is re-pointed at a new `id` in place — so a previous bug's blob URLs are
 * never leaked. A shot that fails to load shows a small inline placeholder
 * instead of throwing.
 *
 * Accessibility: it is a `role="dialog" aria-modal="true"`, closes on Escape,
 * and moves focus to the close button when it opens.
 */

import { useEffect, useRef, useState } from 'react';
import {
  getBug,
  fetchScreenshotBlob,
  BugBoardApiError,
  type MemberSafeBugDetail,
} from '@/services/bugBoardService';
import { StatusPill } from './StatusPill';

interface BugDetailModalProps {
  id: string;
  onClose: () => void;
}

/** Per-screenshot load state: an object URL once loaded, or 'error' on failure. */
type ShotState = string | 'error';

export function BugDetailModal({ id, onClose }: BugDetailModalProps) {
  const [detail, setDetail] = useState<MemberSafeBugDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Keyed by screenshot INDEX (not name): names are unique per the -5 contract,
  // but keying by index keeps this self-contained — duplicate names can never
  // collide in the map or produce duplicate React keys.
  const [shots, setShots] = useState<Record<number, ShotState>>({});
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  // Escape-to-close + focus management. Focus the close button when the modal
  // opens so a keyboard user starts inside the dialog.
  useEffect(() => {
    closeButtonRef.current?.focus();
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [onClose]);

  // Fetch the detail when the id changes.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setDetail(null);
    getBug(id)
      .then((d) => {
        if (cancelled) return;
        setDetail(d);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(
          err instanceof BugBoardApiError
            ? err.message
            : 'Could not load this report. Please try again.',
        );
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  // Once the detail is loaded, fetch each screenshot's authed bytes → object URL.
  // Every URL this run creates is tracked in a RUN-LOCAL array and revoked by
  // THIS run's cleanup, so a `detail`/`id` change (re-render without unmount)
  // frees the previous batch instead of leaking it until final unmount.
  useEffect(() => {
    if (!detail) return;
    let cancelled = false;
    const createdUrls: string[] = [];
    setShots({});
    detail.screenshots.forEach((shot, index) => {
      fetchScreenshotBlob(shot.url)
        .then((blob) => {
          if (cancelled) return;
          const objUrl = URL.createObjectURL(blob);
          createdUrls.push(objUrl);
          setShots((prev) => ({ ...prev, [index]: objUrl }));
        })
        .catch(() => {
          if (cancelled) return;
          setShots((prev) => ({ ...prev, [index]: 'error' }));
        });
    });
    return () => {
      cancelled = true;
      for (const url of createdUrls) {
        URL.revokeObjectURL(url);
      }
    };
  }, [detail]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Bug detail"
      data-testid="board-detail"
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4 sm:p-8"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-2xl rounded-lg bg-white p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          ref={closeButtonRef}
          type="button"
          data-testid="board-detail-close"
          onClick={onClose}
          className="absolute right-4 top-4 rounded px-2 py-1 text-sm text-gray-500 hover:bg-gray-100"
          aria-label="Close"
        >
          Close
        </button>

        {loading && (
          <p data-testid="board-detail-loading" className="py-8 text-center text-sm text-muted-foreground">
            Loading report…
          </p>
        )}

        {error && (
          <div
            role="alert"
            data-testid="board-detail-error"
            className="rounded border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800"
          >
            {error}
          </div>
        )}

        {detail && !loading && !error && (
          <div data-testid="board-detail-body">
            <div className="mb-4 flex items-start gap-3 pr-16">
              <h2 data-testid="board-detail-title" className="flex-1 text-xl font-bold">
                {detail.title}
              </h2>
              <StatusPill status={detail.status} />
            </div>
            <p data-testid="board-detail-id" className="mb-4 font-mono text-xs text-muted-foreground">
              {detail.id}
            </p>

            {detail.fields.length > 0 && (
              <dl data-testid="board-detail-fields" className="mb-6 flex flex-col gap-3">
                {detail.fields.map((field, i) => (
                  <div key={`${field.label}-${i}`} data-testid="board-detail-field">
                    <dt className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      {field.label}
                    </dt>
                    <dd className="whitespace-pre-wrap text-sm">{field.value}</dd>
                  </div>
                ))}
              </dl>
            )}

            {detail.screenshots.length > 0 && (
              <div data-testid="board-detail-shots" className="flex flex-col gap-3">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Screenshots
                </h3>
                <div className="flex flex-wrap gap-3">
                  {detail.screenshots.map((shot, index) => {
                    const state = shots[index];
                    if (state === 'error') {
                      return (
                        <div
                          key={`${shot.name}-${index}`}
                          data-testid="board-detail-shot-error"
                          className="flex h-24 w-32 items-center justify-center rounded border border-dashed border-gray-300 bg-gray-50 text-xs text-muted-foreground"
                        >
                          Image unavailable
                        </div>
                      );
                    }
                    if (typeof state === 'string') {
                      return (
                        <img
                          key={`${shot.name}-${index}`}
                          data-testid="board-detail-shot"
                          src={state}
                          alt={shot.name}
                          className="h-24 w-32 rounded border border-gray-200 object-cover"
                        />
                      );
                    }
                    return (
                      <div
                        key={`${shot.name}-${index}`}
                        data-testid="board-detail-shot-loading"
                        className="flex h-24 w-32 items-center justify-center rounded border border-gray-200 bg-gray-50 text-xs text-muted-foreground"
                      >
                        Loading…
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
